import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {noop} from '@Utils/general.util';
import type {PosterPageData} from '@Libraries/poster-library';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {DesignGallery} from '@Components/design-gallery';
import {selectTab} from '@Components/tabs/tabs-reducer';
import type {DesignSelectorProps, LimitExceededAction} from '@Components/design-selector/design-selector.types';
import {containerQuery, DesignSelectorTab} from '@Components/design-selector/design-selector.types';
import {GalleryThemeURI} from '@Components/design-gallery/design-gallery.types';
import {DesignSelectorSkeletonLoader} from './components/design-selector-skeleton-loader/design-selector-skeleton-loader';
import {DesignSelectorTabSwitcher} from './components/design-selector-tab-switcher/design-selector-tab-switcher';
import styles from './design-selector.module.scss';
import {useAppDispatch} from '@/hooks';
import '@Global/create-from-scratch';
import '@Global/toast';
import type {DesignSelection} from '@Components/social-media/social-media.types';

const LOAD_POSTERS_IF_NOT_LOADED_CUSTOM_EVENT = 'loadDesignsForDesignSelectorIfNotLoaded';
const DESIGN_SELECTOR_ELEMENT_TABS_ID = 'design-selector-element-tabs';

function toDesignSelection(entry: PosterPageData): DesignSelection {
  return {posterHashedId: entry.posterId, pageHashedId: entry.pageId};
}

export function DesignSelector({
  onDesignsSelection = noop,
  initialDesignSelections = [],
  containerRef,
  onGalleryTemplateSelectionCallback = noop,
  onTabSelected = noop,
  designSelectorTab = DesignSelectorTab.MY_DESIGNS,
  startDesignFromMediaModalBtnOpts,
  isLoading = false,
  designGalleryStartingURI = GalleryThemeURI.DEFAULT,
  hideTabContainer = false,
  desktopClasses = '',
  mobileClasses = '',
  designGalleryHeight = '80vh',
  maxSelection,
  onLimitExceeded,
  scrollContainerEl = null,
}: DesignSelectorProps): ReactElement {
  const params = useContainerQuery(containerRef, containerQuery);
  const [areMyDesignsLoaded, setAreMyDesignsLoaded] = useState<boolean>(false);
  const [myDesignsLength, setMyDesignsLength] = useState<number>(0);
  const [areSharedWithMeDesignsLoaded, setAreSharedWithMeDesignsLoaded] = useState(false);
  const dispatch = useAppDispatch();

  const isMyDesignsSelected = (): boolean => {
    return designSelectorTab === DesignSelectorTab.MY_DESIGNS;
  };

  const isTeamSelected = (): boolean => {
    return designSelectorTab === DesignSelectorTab.SHARED_WITH_ME;
  };

  const isDesignGallerySelected = (): boolean => {
    return designSelectorTab === DesignSelectorTab.GALLERY_TEMPLATES;
  };

  const shouldShowLoader = (): boolean => {
    if (isDesignGallerySelected()) {
      return false;
    }
    if (isMyDesignsSelected() && !areMyDesignsLoaded) {
      return true;
    }
    return isTeamSelected() && !areSharedWithMeDesignsLoaded;
  };

  const getMyDesignsLength = (): number => {
    return containerRef.current?.querySelectorAll('.js-poster[data-id]').length ?? 0;
  };

  const onDesignsSelectionRef = useRef(onDesignsSelection);
  const onLimitExceededRef = useRef(onLimitExceeded);
  const maxSelectionRef = useRef(maxSelection);
  const startDesignFromMediaModalBtnOptsRef = useRef(startDesignFromMediaModalBtnOpts);

  useEffect(() => { onDesignsSelectionRef.current = onDesignsSelection; });
  useEffect(() => { onLimitExceededRef.current = onLimitExceeded; });
  useEffect(() => { maxSelectionRef.current = maxSelection; });
  useEffect(() => { startDesignFromMediaModalBtnOptsRef.current = startDesignFromMediaModalBtnOpts; });

  const pendingSelectionRef = useRef<(PosterPageData | DesignSelection)[] | null>(null);

  const openStartDesignFromUploadModal = (): void => {
    const uploadModalOpts = startDesignFromMediaModalBtnOptsRef.current;

    if (uploadModalOpts) {
      window.PMW.triggerCreateDesignFromUserUpload(uploadModalOpts);
    }
  };

  useEffect(() => {
    let dispose: VoidFunction | undefined;
    let cancelled = false;

    window.PMW.util.require(typeof window.PMW.designSelector === 'undefined', 'designSelector', true, () => {
      if (cancelled) {
        return;
      }
      const initialLimitExceededHandler = onLimitExceededRef.current;
      dispose = window.PMW.designSelector.openEmbedded({
        divToLoadIn: containerRef.current?.querySelector('.js-design-selector-element') ?? '.js-design-selector-element',
        scrollContainerEl: scrollContainerEl ?? containerRef.current,
        loadPostersIfNotLoadedEventType: LOAD_POSTERS_IF_NOT_LOADED_CUSTOM_EVENT,
        preselectedDesigns: initialDesignSelections,
        isMultiSelectionAllowed: maxSelectionRef.current !== 1,
        addCheckmarkOnSelection: true,
        selectedNameClass: styles.selectedName,
        maxSelection: maxSelectionRef.current,
        onLimitExceeded: initialLimitExceededHandler
          ? (info): LimitExceededAction => {
              return (onLimitExceededRef.current ?? initialLimitExceededHandler)({
                currentSelection: info.currentSelection.map(toDesignSelection),
                tentativeSelection: info.tentativeSelection.map(toDesignSelection),
                newlyAdded: info.newlyAdded.map(toDesignSelection),
                maxSelection: info.maxSelection,
                isBulkAdd: info.isBulkAdd,
              });
            }
          : undefined,
        startFromMediaModalBtnOpts: startDesignFromMediaModalBtnOptsRef.current,
        onSelectionChange: (selection): void => {
          onDesignsSelectionRef.current(selection.map(toDesignSelection));
        },
        onLoaded: (): void => {
          setAreMyDesignsLoaded(true);
          if (pendingSelectionRef.current !== null) {
            window.PMW.designSelector.setSelection(pendingSelectionRef.current);
            pendingSelectionRef.current = null;
          }
        },
      });
    });

    return (): void => {
      cancelled = true;
      dispose?.();
    };
  }, []);

  useEffect(() => {
    if (!areMyDesignsLoaded) {
      return;
    }
    const raf = requestAnimationFrame(() => {
      setMyDesignsLength(getMyDesignsLength());
    });
    return (): void => { cancelAnimationFrame(raf); };
  }, [areMyDesignsLoaded]);

  useDidUpdateEffect(() => {
    if (typeof window.PMW.designSelector === 'undefined') {
      pendingSelectionRef.current = initialDesignSelections;
      return;
    }
    window.PMW.designSelector.setSelection(initialDesignSelections);
  }, [initialDesignSelections]);

  useEffect(() => {
    dispatch(selectTab(DESIGN_SELECTOR_ELEMENT_TABS_ID, designSelectorTab));
  }, [designSelectorTab]);

  useDidUpdateEffect(() => {
    if (!shouldShowLoader()) {
      return;
    }

    let cancelled = false;

    window.dispatchEvent(
      new CustomEvent(LOAD_POSTERS_IF_NOT_LOADED_CUSTOM_EVENT, {
        detail: {
          onDesignLoadedCallback: (): void => {
            if (cancelled) {
              return;
            }
            if (isMyDesignsSelected()) {
              setAreMyDesignsLoaded(true);
            } else if (isTeamSelected()) {
              setAreSharedWithMeDesignsLoaded(true);
            }
          },
        },
      })
    );

    return (): void => {
      cancelled = true;
    };
  }, [designSelectorTab]);

  const getDesignSelectorLoadingClass = (): string => {
    if (isLoading) {
      return styles.isLoadingDesignSelector;
    }

    return '';
  };

  const getDesignSelectorDesktopClasses = (): string => {
    return `${styles.designSelectorDesktop} ${desktopClasses}`;
  };

  const getDesignSelectorMobileClasses = (): string => {
    return `${styles.designSelectorMobile} ${mobileClasses}`;
  };

  const getDesignSelectorClasses = (): string => {
    let classes = getDesignSelectorLoadingClass();

    if (params.small) {
      classes += ` ${getDesignSelectorMobileClasses()}`;
    } else {
      classes += ` ${getDesignSelectorDesktopClasses()}`;
    }

    if (isDesignGallerySelected()) {
      classes += ` ${styles.templateGallerySelector}`;
    }

    return classes;
  };

  const getDesignSelectorElementContentClasses = (): string => {
    const visibility = isMyDesignsSelected() || isTeamSelected() ? '' : '_hidden';
    return `js-design-selector-element design-selector-element ${styles.designSelectorElementContent} ${visibility}`;
  };

  const getMystuffPostersClasses = (): string => {
    const visibility = isMyDesignsSelected() ? 'selected' : '_hidden';
    const emptyState = myDesignsLength ? '' : styles.emptyMyDesigns;
    return `mystuff-posters ${visibility} ${emptyState}`;
  };

  const getSharedWithMePostersClasses = (): string => {
    return `shared-with-me-posters ${isTeamSelected() ? 'selected' : '_hidden'}`;
  };

  const getGalleryTemplateSelectorClasses = (): string => {
    return `${styles.galleryTemplateSelector} ${isDesignGallerySelected() ? '' : '_hidden'}`;
  };

  const getDesignSelector = (): ReactElement => {
    return (
      <div className={getDesignSelectorClasses()}>
        <DesignSelectorTabSwitcher
          designSelectorTab={designSelectorTab}
          onTabSelected={onTabSelected}
          isMobile={params.small}
          hideTabContainer={hideTabContainer}
          tabsId={DESIGN_SELECTOR_ELEMENT_TABS_ID}
        />
        <DesignSelectorSkeletonLoader isVisible={shouldShowLoader()} containerRef={containerRef} />
        <div className={getDesignSelectorElementContentClasses()}>
          <div className={getMystuffPostersClasses()} />
          <div className={getSharedWithMePostersClasses()} />
        </div>

        <div className={getGalleryTemplateSelectorClasses()}>
          <DesignGallery
            disableCreateFromScratchCTA
            onDesignsSelected={onGalleryTemplateSelectionCallback}
            onUploadMediaClick={startDesignFromMediaModalBtnOpts ? openStartDesignFromUploadModal : undefined}
            height={designGalleryHeight}
            startingPageURI={designGalleryStartingURI}
          />
        </div>
      </div>
    );
  };

  return <div ref={containerRef}>{getDesignSelector()}</div>;
}
